/**
 * CloudSyncScreen — `/settings/sync`.
 *
 * Pixel-faithful port adapted from `design/project/screens-features.jsx`'s
 * `CloudSyncScreen`. We surface the data the spec actually requires:
 *
 *   - `meta.last_sync_at` (relative humanised timestamp).
 *   - Pending queue size + the last 3 op types currently in flight / queued.
 *   - Recent 10 `sync_failed` notifications + a per-row 「重試」 button.
 *   - Manual 「立即同步」 button — calls the externally provided `onManualSync`
 *     (parent owns the `SyncQueue` instance and exposes `drain()`).
 *
 * Sharp edges:
 *   - We MUST mask any auth token / bearer / api-key in the displayed
 *     `last_error` text — see `maskAuthSecrets()`. (Audit requirement.)
 *   - "重試" re-enqueues the failed op with `attempts=0` and removes the
 *     corresponding `sync_failed` notification.
 */
import { useMemo } from 'react';

import Icon from '@/components/common/Icon';
import { useStore } from '@/state/store';
import type {
  NotificationRow,
  TransactionRow,
  RecurringRow,
} from '@/lib/sheets/types';
import type { LegacySyncOp as SyncOp, SyncOpType } from '@/lib/sheets/sync-queue';

export interface CloudSyncScreenProps {
  onBack?: () => void;
  /** Trigger queue drain. Parent owns the queue handle. */
  onManualSync?: () => Promise<void> | void;
}

function maskAuthSecrets(s: string): string {
  return s
    .replace(/(authorization\s*[:=]\s*)bearer\s+\S+/gi, '$1bearer ***')
    .replace(/(Bearer\s+)[A-Za-z0-9._-]+/g, '$1***')
    .replace(/(api[_-]?key\s*[:=]\s*)\S+/gi, '$1***')
    .replace(/(token\s*[:=]\s*)\S+/gi, '$1***');
}

function formatRelative(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '尚未同步';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = now.getTime() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 30) return '剛剛';
  if (sec < 60) return `${sec} 秒前`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} 分鐘前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小時前`;
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const OP_TYPE_LABELS: Partial<Record<SyncOpType, string>> = {
  create_tx: '新增交易',
  update_tx: '更新交易',
  delete_tx: '刪除交易',
  create_book: '新增帳本',
  update_book: '更新帳本',
  delete_book: '刪除帳本',
  create_category: '新增分類',
  update_category: '更新分類',
  delete_category: '刪除分類',
  create_recurring: '新增固定週期',
  update_recurring: '更新固定週期',
  delete_recurring: '刪除固定週期',
  create_notification: '寫入通知',
};

function genOpId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function CloudSyncScreen({
  onBack,
  onManualSync,
}: CloudSyncScreenProps): JSX.Element {
  const lastSyncAt = useStore((s) => s.sync.lastSyncAt);
  const pending = useStore((s) => s.sync.pending);
  const enqueue = useStore((s) => s.sync.enqueue);
  const removePending = useStore((s) => s.sync.removePending);
  const notifs = useStore((s) => s.notifications.items);
  const markRead = useStore((s) => s.notifications.markRead);

  const recentFails = useMemo<NotificationRow[]>(() => {
    return [...notifs]
      .filter((n) => n.kind === 'sync_failed')
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
      .slice(0, 10);
  }, [notifs]);

  const last3 = pending.slice(0, 3);

  const retry = (n: NotificationRow): void => {
    let parsed: { op?: string; op_id?: string; payload?: unknown } = {};
    try {
      parsed = JSON.parse(n.payload || '{}');
    } catch {
      /* noop */
    }
    if (typeof parsed.op !== 'string') return;
    const ts = new Date().toISOString();
    enqueue({
      id: genOpId(),
      type: parsed.op as SyncOpType,
      payload: (parsed.payload ?? {}) as
        | TransactionRow
        | RecurringRow
        | Record<string, unknown>,
      attempts: 0,
      last_error: '',
      created_at: ts,
    });
    // Drop the prior failed op (if it's still being tracked).
    if (typeof parsed.op_id === 'string') {
      removePending(parsed.op_id);
    }
    markRead(n.id);
  };

  return (
    <div
      style={{
        height: '100%',
        overflow: 'auto',
        background: 'var(--bg-app)',
        paddingBottom: 80,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '60px 20px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="返回"
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Icon name="chevL" size={22} stroke="var(--fg-1)" />
          </button>
        )}
        <h1
          style={{
            font: '600 22px var(--font-cjk-display)',
            color: 'var(--fg-1)',
            letterSpacing: '-0.01em',
            margin: 0,
          }}
        >
          雲端同步
        </h1>
      </div>

      <div style={{ padding: '8px 16px 24px' }}>
        {/* Status card */}
        <div
          style={{
            background: 'var(--mocha-700)',
            color: 'var(--cream-50)',
            borderRadius: 18,
            padding: '18px 20px',
          }}
        >
          <div
            style={{
              font: '500 11px var(--font-cjk)',
              color: 'var(--fg-on-inverse-2)',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            最後同步時間
          </div>
          <div
            style={{
              font: '600 22px var(--font-cjk)',
              marginTop: 4,
              letterSpacing: '-0.01em',
            }}
          >
            {formatRelative(lastSyncAt)}
          </div>
          {pending.length > 0 && (
            <div
              style={{
                marginTop: 8,
                font: '400 12.5px var(--font-cjk)',
                color: 'var(--fg-on-inverse-2)',
              }}
            >
              待寫入：{pending.length} 項
            </div>
          )}
          <button
            type="button"
            onClick={() => onManualSync?.()}
            style={{
              marginTop: 14,
              padding: '8px 16px',
              background: 'rgba(251,247,241,0.14)',
              border: '0.5px solid rgba(251,247,241,0.2)',
              borderRadius: 999,
              color: 'var(--cream-50)',
              font: '500 13px var(--font-cjk)',
              cursor: 'pointer',
            }}
          >
            立即同步
          </button>
        </div>

        {/* Pending */}
        {pending.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <SectionHeader>待寫入佇列 · {pending.length}</SectionHeader>
            <Card>
              {last3.map((op, i) => (
                <PendingRow
                  key={op.id}
                  op={op}
                  isLast={i === last3.length - 1 && pending.length <= 3}
                />
              ))}
              {pending.length > 3 && (
                <div
                  style={{
                    padding: '10px 14px',
                    font: '400 12px var(--font-cjk)',
                    color: 'var(--fg-3)',
                    textAlign: 'center',
                  }}
                >
                  以及其他 {pending.length - 3} 筆…
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Failed */}
        <div style={{ marginTop: 18 }}>
          <SectionHeader>最近失敗 · {recentFails.length}</SectionHeader>
          {recentFails.length === 0 ? (
            <Card>
              <div
                style={{
                  padding: '20px 14px',
                  font: '400 13px var(--font-cjk)',
                  color: 'var(--fg-3)',
                  textAlign: 'center',
                }}
              >
                沒有最近失敗紀錄
              </div>
            </Card>
          ) : (
            <Card>
              {recentFails.map((n, i) => (
                <FailRow
                  key={n.id}
                  notif={n}
                  isLast={i === recentFails.length - 1}
                  onRetry={() => retry(n)}
                />
              ))}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        font: '600 12px var(--font-cjk)',
        color: 'var(--fg-2)',
        letterSpacing: '0.06em',
        padding: '0 4px 8px',
      }}
    >
      {children}
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 16,
        boxShadow: 'var(--shadow-1)',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

function PendingRow({
  op,
  isLast,
}: {
  op: SyncOp;
  isLast: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderBottom: isLast ? 0 : '0.5px solid var(--border-1)',
      }}
    >
      <Icon name="cloud" size={18} stroke="var(--fg-2)" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            font: '500 14px var(--font-cjk)',
            color: 'var(--fg-1)',
          }}
        >
          {OP_TYPE_LABELS[op.type] ?? op.type}
        </div>
        <div
          style={{
            font: '400 11.5px var(--font-cjk)',
            color: 'var(--fg-3)',
            marginTop: 2,
          }}
        >
          佇列 {formatRelative(op.created_at)}
          {op.attempts > 0 ? ` · 已重試 ${op.attempts} 次` : ''}
        </div>
      </div>
    </div>
  );
}

function FailRow({
  notif,
  isLast,
  onRetry,
}: {
  notif: NotificationRow;
  isLast: boolean;
  onRetry: () => void;
}): JSX.Element {
  let opLabel = '同步失敗';
  let errLine = '';
  try {
    const p = JSON.parse(notif.payload || '{}') as Record<string, unknown>;
    if (typeof p.op === 'string') {
      opLabel =
        OP_TYPE_LABELS[p.op as SyncOpType] ?? `${p.op} 失敗`;
    }
    if (typeof p.error === 'string') {
      errLine = maskAuthSecrets(p.error);
    }
  } catch {
    /* noop */
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 14px',
        borderBottom: isLast ? 0 : '0.5px solid var(--border-1)',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          background: 'var(--clay-500)',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        <Icon name="cloudOff" size={16} stroke="#fff" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            font: '500 14px var(--font-cjk)',
            color: 'var(--fg-1)',
          }}
        >
          {opLabel}
        </div>
        {errLine && (
          <div
            style={{
              font: '400 11.5px var(--font-cjk)',
              color: 'var(--fg-3)',
              marginTop: 2,
              wordBreak: 'break-word',
              lineHeight: 1.45,
            }}
          >
            {errLine}
          </div>
        )}
        <div
          style={{
            font: '400 11px var(--font-cjk)',
            color: 'var(--fg-4)',
            marginTop: 4,
          }}
        >
          {formatRelative(notif.created_at)}
        </div>
      </div>
      <button
        type="button"
        onClick={onRetry}
        style={{
          padding: '6px 12px',
          background: 'transparent',
          border: '0.5px solid var(--accent)',
          borderRadius: 999,
          color: 'var(--accent)',
          font: '600 12px var(--font-cjk)',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        重試
      </button>
    </div>
  );
}
